import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { api } from "@/api/client";
import type {
  CreateTeamMemberRequest,
  CreateTeamMemberResponse,
  InviteExternalTeamMemberRequest,
  SettingsTeamResponse,
  TeamInviteResponse,
  TeamMember,
  UpdateTeamMemberRequest,
} from "@/api/types";
import { isErrorEnvelope } from "@/lib/handle-form-error";
import { businessSlugFromOverview, useDashboardRequest } from "./dashboard-request";

function invalidateTeamQueries(queryClient: QueryClient, slug: string | undefined) {
  void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (slug) {
    void queryClient.invalidateQueries({ queryKey: ["businesses", slug, "team"] });
    void queryClient.invalidateQueries({ queryKey: ["businesses", slug, "availability"] });
  }
}

export function isSavedTeamInvitationError(error: unknown): boolean {
  return (
    error instanceof HTTPError &&
    isErrorEnvelope(error.data) &&
    error.data.errors.some((entry) => entry.code === "invitation_delivery_unavailable")
  );
}

export function useDashboardTeam() {
  const dashboard = useDashboardRequest();

  return useQuery({
    queryKey: dashboard.queryKey("team"),
    queryFn: () =>
      api.get("dashboard/team", dashboard.requestOptions()).json<SettingsTeamResponse>(),
    staleTime: 60_000,
  });
}

export function useCreateDashboardTeamMember() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (data: CreateTeamMemberRequest) =>
      api
        .post("dashboard/team", { ...dashboard.requestOptions(), json: data })
        .json<CreateTeamMemberResponse>(),
    onSettled: (_data, error) => {
      if (error && !isSavedTeamInvitationError(error)) return;
      invalidateTeamQueries(queryClient, businessSlugFromOverview(queryClient, dashboard.slug));
    },
  });
}

export function useInviteExternalTeamMember() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: ({ team_id, email }: InviteExternalTeamMemberRequest) =>
      api
        .post(`dashboard/team/${team_id}/invite`, {
          ...dashboard.requestOptions(),
          json: { email },
        })
        .json<TeamInviteResponse>(),
    onSettled: (_data, error) => {
      if (error && !isSavedTeamInvitationError(error)) return;
      invalidateTeamQueries(queryClient, businessSlugFromOverview(queryClient, dashboard.slug));
    },
  });
}

export function useResendTeamInvite() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (id: string) =>
      api
        .post(`dashboard/team/invites/${id}/resend`, dashboard.requestOptions())
        .json<TeamInviteResponse>(),
    onSettled: (_data, error) => {
      if (error && !isSavedTeamInvitationError(error)) return;
      invalidateTeamQueries(queryClient, businessSlugFromOverview(queryClient, dashboard.slug));
    },
  });
}

export function useRevokeTeamInvite() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (id: string) =>
      api
        .delete(`dashboard/team/invites/${id}`, dashboard.requestOptions())
        .json<TeamInviteResponse>(),
    onSuccess: () => {
      invalidateTeamQueries(queryClient, businessSlugFromOverview(queryClient, dashboard.slug));
    },
  });
}

export function useUpdateDashboardTeamMember() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateTeamMemberRequest & { id: string }) =>
      api
        .patch(`dashboard/team/${id}`, { ...dashboard.requestOptions(), json: data })
        .json<TeamMember>(),
    onSuccess: () => {
      invalidateTeamQueries(queryClient, businessSlugFromOverview(queryClient, dashboard.slug));
    },
  });
}

export function useRemoveDashboardTeamMember() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`dashboard/team/${id}`, dashboard.requestOptions()).json<{ id: string }>(),
    onSuccess: () => {
      invalidateTeamQueries(queryClient, businessSlugFromOverview(queryClient, dashboard.slug));
    },
  });
}
